import { api } from './client';
import type { Page } from './catalogs';

/**
 * ч.1386: документ «Фінансова звітність» (подання до ДПС).
 * values: {код поля XSD (R1000G4): 'грн.коп'}; у XML — тис. грн.
 */
export type FinFormSet = 'MICRO' | 'SMALL' | 'FULL';

export interface FinStatementFields {
  name?: string; tin?: string;
  /** ч.1390: на бланку — КАТОТТГ; КОАТУУ (10 цифр) — лише для файла ДПС (схема досі його вимагає). */
  katottg?: string; koatuu?: string; koatuu_name?: string;
  kopfg?: string; kopfg_name?: string;
  kved?: string; kved_name?: string;
  kil?: number | null;
  loc?: string; tel?: string;
  standards?: 'NPSBO' | 'IFRS';
}

export interface FinancialStatement {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  form_set: FinFormSet;
  report_type: 'REPORTING' | 'REPORTING_NEW' | 'CORRECTING';
  period_year: number;
  period_quarter: number;
  doc_cnt: number;
  values: Record<string, string>;
  fields: FinStatementFields;
  warnings: string[];
  comment: string | null;
  deletion_mark: boolean;
  created_at: string;
}

export interface FinReportLineDef {
  line_code: string;
  line_name: string;
  section: string | null;
  order_no: number;
  is_total: boolean;
  is_negative: boolean;
  parent_line: string | null;
  is_manual: boolean;
}

export interface FinFormDef {
  form_code: string;
  form_name: string;
  xsd_code: string;
  /** matrix — Ф4 «Звіт про власний капітал»: графи G3…G10 (ч.1388) */
  kind: 'balance' | 'turnover' | 'matrix';
  decimals: number;
  lines: FinReportLineDef[];
}

/** Ф4: графи матриці (код у назві поля → заголовок). */
export const EQUITY_COLUMNS: { col: string; title: string }[] = [
  { col: 'G3', title: 'Зареєстрований капітал' }, { col: 'G4', title: 'Капітал у дооцінках' },
  { col: 'G5', title: 'Додатковий капітал' }, { col: 'G6', title: 'Резервний капітал' },
  { col: 'G7', title: 'Нерозподілений прибуток (непокритий збиток)' }, { col: 'G8', title: 'Неоплачений капітал' },
  { col: 'G9', title: 'Вилучений капітал' }, { col: 'G10', title: 'Всього' },
];

export interface FinStatementCheck {
  errors: { form: string; message: string }[];
  warnings: { form: string; message: string }[];
}

export const FORM_SET_TITLES: Record<FinFormSet, string> = {
  MICRO: 'мікропідприємство (№1-мс, №2-мс)',
  SMALL: 'мале підприємство (№1-м, №2-м)',
  FULL: 'повна (Баланс №1, Звіт про фінрезультати №2; за рік — ще №3 і №4)',
};
export const FORM_SET_FORMS: Record<FinFormSet, [string, string]> = {
  MICRO: ['1ms', '2ms'], SMALL: ['1m', '2m'], FULL: ['1', '2'],
};
/** Форми документа: повний набір за рік — ще Ф3 (рух грошових коштів) і Ф4 (власний капітал) (ч.1388). */
export function formsFor(set: FinFormSet, quarter: number): string[] {
  return set === 'FULL' && quarter === 4 ? ['1', '2', '3', '4'] : [...FORM_SET_FORMS[set]];
}
export const FORM_TABS: Record<string, string> = {
  '1': 'Баланс', '1m': 'Баланс', '1ms': 'Баланс',
  '2': 'Звіт про фінансові результати', '2m': 'Звіт про фінансові результати', '2ms': 'Звіт про фінансові результати',
  '3': 'Рух грошових коштів', '4': 'Власний капітал',
};
export const PERIOD_LABELS: Record<number, string> = {
  1: 'І квартал', 2: 'півріччя', 3: '9 місяців', 4: 'рік',
};
export const BALANCE_DATE: Record<number, string> = {
  1: '31 березня', 2: '30 червня', 3: '30 вересня', 4: '31 грудня',
};

export const financialStatements = {
  list: (params?: {
    organization_id?: string; period_year?: number; period_quarter?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) =>
    api.get<Page<FinancialStatement>>('/financial-statements', { params }).then((r) => r.data),
  forms: () => api.get<FinFormDef[]>('/financial-statements/forms').then((r) => r.data),
  get: (id: string) =>
    api.get<FinancialStatement>(`/financial-statements/${id}`).then((r) => r.data),
  create: (p: {
    organization_id: string; period_year: number; period_quarter: number;
    form_set?: FinFormSet | null; report_type?: string; comment?: string | null;
  }) =>
    api.post<FinancialStatement>('/financial-statements', p).then((r) => r.data),
  update: (id: string, p: {
    report_type?: string; comment?: string | null; document_date?: string;
    values?: Record<string, string>; fields?: FinStatementFields;
  }) =>
    api.patch<FinancialStatement>(`/financial-statements/${id}`, p).then((r) => r.data),
  recalc: (id: string) =>
    api.post<FinancialStatement>(`/financial-statements/${id}/recalc`).then((r) => r.data),
  check: (id: string) =>
    api.get<FinStatementCheck>(`/financial-statements/${id}/check`).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/financial-statements/${id}`).then(() => undefined),
  xml: (id: string) =>
    api.get(`/financial-statements/${id}/xml`, { responseType: 'blob' }),
  files: (id: string) =>
    api.get<{ files: { fname: string; xml_base64: string }[] }>(
      `/financial-statements/${id}/files`).then((r) => r.data),
  submission: (id: string) =>
    api.get<{ submission_id: string | null; status: string | null;
      receipt_accepted?: boolean | null }>(
      `/financial-statements/${id}/submission`).then((r) => r.data),
};

/** Сума у тис. грн, як у бланку: без десяткового знака (Ф1/Ф2) або з одним (1-м, 1-мс). */
export function toThousands(v: string | number | undefined, decimals: number): string {
  const n = Number(v ?? 0);
  if (!n) return '—';
  const t = n / 1000;
  const r = decimals === 0 ? Math.round(t) : Math.round(t * 10) / 10;
  return r.toLocaleString('uk-UA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function fmtUah(v: string | number | undefined): string {
  const n = Number(v ?? 0);
  if (!n) return '—';
  return n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
